import React from 'react';
import { 
    Box, 
    Typography, 
    TextField, 
    Grid, 
    Paper,
    Button,
    IconButton,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

/**
 * Address Information step for company onboarding
 */
export const AddressStep = ({ 
    addresses,
    setAddresses,
    errors
}) => {
    // Add a new address
    const handleAddAddress = () => {
        setAddresses([
            ...addresses,
            { 
                street: '', 
                city: '', 
                state: '', 
                zipCode: '',
                isPrimary: addresses.length === 0
            }
        ]);
    };

    // Delete an address
    const handleDeleteAddress = (index) => {
        const newAddresses = [...addresses];
        newAddresses.splice(index, 1);
        
        // If we deleted the primary address, make the first one primary
        if (newAddresses.length > 0 && addresses[index].isPrimary) {
            newAddresses[0].isPrimary = true;
        }
        
        setAddresses(newAddresses);
    };

    // Update an address field
    const handleAddressChange = (index, field, value) => {
        const newAddresses = [...addresses];
        newAddresses[index] = {
            ...newAddresses[index],
            [field]: value
        };
        setAddresses(newAddresses);
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                    Company Addresses
                </Typography>
                <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={handleAddAddress}
                >
                    Add Address
                </Button>
            </Box>
            
            {addresses.length === 0 && (
                <Typography color="text.secondary" sx={{ my: 3 }}>
                    Please add your company's main address to continue.
                </Typography>
            )}
            
            {addresses.map((address, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                    {index > 0 && <Divider sx={{ my: 2 }} />}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            {address.isPrimary ? 'Primary Address' : `Additional Address ${index}`}
                        </Typography>
                        
                        {!address.isPrimary && (
                            <IconButton
                                color="error"
                                onClick={() => handleDeleteAddress(index)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Street Address"
                                value={address.street}
                                onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                                required
                                error={Boolean(errors?.addresses?.[index]?.street)}
                                helperText={errors?.addresses?.[index]?.street}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                value={address.city}
                                onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                                required
                                error={Boolean(errors?.addresses?.[index]?.city)}
                                helperText={errors?.addresses?.[index]?.city}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="State/Province"
                                value={address.state}
                                onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                                required
                                error={Boolean(errors?.addresses?.[index]?.state)}
                                helperText={errors?.addresses?.[index]?.state}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="ZIP/Postal Code"
                                value={address.zipCode}
                                onChange={(e) => handleAddressChange(index, 'zipCode', e.target.value)}
                                required
                                error={Boolean(errors?.addresses?.[index]?.zipCode)}
                                helperText={errors?.addresses?.[index]?.zipCode}
                            />
                        </Grid>
                    </Grid>
                </Box>
            ))}
            
            {addresses.length === 0 && (
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={handleAddAddress}
                    sx={{ mt: 2 }}
                >
                    Add Primary Address
                </Button>
            )}
        </Paper>
    );
}; 